import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { useTheme } from '../context/themeContext';
import Icon from './icon';

export interface BreadcrumbItem {
  label: string;
  onPress?: () => void;
}

interface PageHeaderProps {
  title: string;
  sub?: string;
  actions?: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  style?: ViewStyle;
}

export default function PageHeader({
  title,
  sub,
  actions,
  breadcrumbs,
  style,
}: PageHeaderProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;

  const breadcrumbTextStyle: TextStyle = {
    fontSize: fontSize.sm,
    color: colors.ink3,
    fontFamily: theme.font.sans,
    includeFontPadding: false,
  };

  const breadcrumbLinkStyle: TextStyle = {
    ...breadcrumbTextStyle,
    ...Platform.select({
      web: { cursor: 'pointer' } as TextStyle,
      default: {},
    }),
  };

  const titleStyle: TextStyle = {
    fontSize: fontSize['2xl'],
    fontWeight: fontWeight.semibold,
    color: colors.ink,
    fontFamily: theme.font.sans,
    letterSpacing: -0.01 * fontSize['2xl'],
    margin: 0,
    includeFontPadding: false,
  };

  const subStyle: TextStyle = {
    fontSize: fontSize.md,
    color: colors.ink3,
    fontFamily: theme.font.sans,
    marginTop: 2,
    includeFontPadding: false,
  };

  return (
    <View style={[{ marginBottom: 16 }, style]}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            marginBottom: 6,
            flexWrap: 'wrap',
          }}
        >
          {breadcrumbs.map((b, i) => (
            <React.Fragment key={i}>
              {i > 0 && (
                <Icon name="chev-r" size={10} color={colors.ink3} />
              )}
              {b.onPress ? (
                <TouchableOpacity onPress={b.onPress} activeOpacity={0.7}>
                  <Text style={breadcrumbLinkStyle}>{b.label}</Text>
                </TouchableOpacity>
              ) : (
                <Text
                  style={[
                    breadcrumbTextStyle,
                    i === breadcrumbs.length - 1 ? { color: colors.ink } : undefined,
                  ]}
                >
                  {b.label}
                </Text>
              )}
            </React.Fragment>
          ))}
        </View>
      )}

      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={titleStyle}>{title}</Text>
          {sub && <Text style={subStyle}>{sub}</Text>}
        </View>
        {actions && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              flexShrink: 0,
            }}
          >
            {actions}
          </View>
        )}
      </View>
    </View>
  );
}
